
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useInView } from '../../hooks/useInView';

const services = [
  {
    icon: 'ri-building-line',
    title: 'Konstrukcje Stalowe',
    description:
      'Oferujemy swoje usługi w zakresie wykonawstwa i montażu wszelkiego rodzaju konstrukcji stalowych oraz montażu urządzeń głównie dla przemysłu energetycznego, hutniczego oraz budownictwa ogólnego.',
    details: [
      'Hale przemysłowe, hangary, hale sportowe i widowiskowe, wiaty magazynowe',
      'Zbiorniki cylindryczne na ciecze i gazy',
      'Rurociągi i kanały odpylające, okrągłe oraz prostokątne',
      'Kominy stalowe',
      'Kratowe wieże antenowe',
      'Zabezpieczenie antykorozyjne konstrukcji',
      'Remonty i modernizacje urządzeń i instalacji przemysłowych'
    ],
    image:
      'https://public.readdy.ai/ai/img_res/876294ac732bcb336616a53e2d4cf5c5.jpg'
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Spawalnictwo',
    description:
      'W naszym zakładzie wykonujemy usługi według najnowszych technik spawalniczych metodami TIG, MIG, MMA.',
    details: [
      'Spawanie stopów aluminium',
      'Spawanie żeliwa (metodą na zimno i na gorąco)',
      'Regeneracja żeliwa metodami niekonwencjonalnymi (metoda na zimno)',
      'Spawanie stali nierdzewnych i kwasoodpornych',
      'Spawanie staliwa',
      'Lutowanie miękkie i twarde',
      'Badania ciśnieniowe płaszcza wodnego głowic (hydrauliczne, pneumatyczne)',
      'Badanie magnetyczne złączy spawanych'
    ],
    image:
      'https://readdy.ai/api/search-image?query=professional%20welder%20working%20with%20bright%20blue%20electric%20sparks%20in%20dark%20industrial%20environment%20close%20up%20precision%20welding%20photorealistic%208k%20cinematic%20quality%20dramatic%20lighting%20no%20text%20no%20logo&width=1200&height=800&seq=service-detail-welding-001&orientation=landscape'
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Obróbka Skrawaniem',
    description:
      'Zrealizujemy każde nawet najbardziej nietypowe zlecenie, zarówno jednostkowe wytworzenie prototypu jak również produkcję mało i średnio seryjną.',
    details: [
      'Obróbka skrawaniem części i podzespołów maszyn dużych i małych gabarytów',
      'Regeneracja i wytwarzanie części maszyn',
      'Obróbka metali: toczenie, frezowanie, roztaczanie, dłutowanie, szlifowanie',
      'Obróbka cieplna',
      'Roboty kowalskie',
      'Wytwarzanie nietypowych maszyn – prototypów',
      'Cięcie plazmą blach ze sterowaniem CNC',
      'Walcowanie blach',
      'Doradztwo technologiczne w zakresie obróbki materiałów'
    ],
    image:
      'https://readdy.ai/api/search-image?query=cnc%20machine%20precision%20cutting%20metal%20with%20blue%20coolant%20spray%20close%20up%20industrial%20manufacturing%20photorealistic%208k%20quality%20dark%20background%20electric%20blue%20lighting%20professional%20photography%20no%20text%20no%20logo&width=1200&height=800&seq=service-detail-cnc-001&orientation=landscape'
  },
  {
    icon: 'ri-tools-line',
    title: 'Oprzyrządowanie',
    description:
      'Oferujemy Państwu możliwość wykonania oprzyrządowania dla zakładów przemysłowych.',
    details: [
      'Przyrządy obróbkowe: tokarskie, frezarskie, wiertarskie, szlifierskie, spawalnicze',
      'Przyrządy montażowe: wykrojniki jedno i wielotaktowe',
      'Urządzenia technologiczne: stoiska montażowe, tłoczniki, wykrojniki',
      'Narzędzia specjalne do obróbki skrawaniem',
      'Narzędzia specjalne kształtowe: frezy ślimakowe, dłutaki, przeciągacze',
      'Pomoce kontrolne: stoiska kontrolno-pomiarowe, przyrządy kontrolne',
      'Formy do tworzyw i gumy, kokile do odlewania metali'
    ],
    image:
      'https://readdy.ai/api/search-image?query=precision%20industrial%20tools%20and%20equipment%20arranged%20on%20metallic%20workbench%20with%20dramatic%20blue%20lighting%20professional%20manufacturing%20setup%20photorealistic%208k%20quality%20dark%20background%20no%20text%20no%20logo&width=1200&height=800&seq=service-detail-tools-001&orientation=landscape'
  }
];

export default function ServicesPage() {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    // Scroll to top on mount; wrapped in a try/catch to avoid errors in non‑browser environments
    try {
      window.scrollTo(0, 0);
    } catch (e) {
      console.warn('Unable to scroll to top:', e);
    }
  }, []);

  return (
    <>
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-20 bg-[#050505] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=advanced%20industrial%20manufacturing%20facility%20interior%20with%20modern%20machinery%20and%20blue%20atmospheric%20lighting%20cinematic%20wide%20angle%20photorealistic%208k%20quality%20dark%20moody%20atmosphere%20no%20text%20no%20logo&width=1920&height=1080&seq=services-hero-001&orientation=landscape"
            alt="Usługi"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center"
          >
            <span
              className="text-[#00E5FF] text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              Nasze Usługi
            </span>
            <h1
              className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mt-3 sm:mt-4 leading-tight"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Kompleksowe <span className="text-[#00E5FF]">Rozwiązania</span>
            </h1>
            <p
              className="text-sm sm:text-lg lg:text-xl text-[#C0C7CF] mt-4 sm:mt-6 max-w-3xl mx-auto px-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Zaawansowane technologie produkcji dla wymagającego przemysłu
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section ref={ref} className="relative py-12 sm:py-16 lg:py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="space-y-16 sm:space-y-24 lg:space-y-32">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: index * 0.15 }}
                className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center"
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative group">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[250px] sm:h-[350px] lg:h-[500px] object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4 sm:top-8 sm:left-8 w-14 h-14 sm:w-20 sm:h-20 flex items-center justify-center bg-[#00E5FF]/90 border-2 border-white">
                      <i className={`${service.icon} text-2xl sm:text-4xl text-white`}></i>
                    </div>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="mb-4 sm:mb-6">
                    <span
                      className="text-[#00E5FF] text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold"
                      style={{ fontFamily: 'Orbitron, sans-serif' }}
                    >
                      Usługa {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="h-[2px] w-16 sm:w-20 bg-[#00E5FF] mt-3"></div>
                  </div>

                  <h2
                    className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight"
                    style={{ fontFamily: 'Rajdhani, sans-serif' }}
                  >
                    {service.title}
                  </h2>

                  <p
                    className="text-[#C0C7CF] text-sm sm:text-base leading-relaxed mb-5 sm:mb-8"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {service.description}
                  </p>

                  <div className="space-y-2.5 sm:space-y-4">
                    {service.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start gap-3 sm:gap-4">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00E5FF] mt-1.5 sm:mt-2 flex-shrink-0"></div>
                        <p
                          className="text-[#C0C7CF] text-xs sm:text-sm"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {detail}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 sm:mt-10">
                    <a
                      href="/kontakt"
                      className="inline-block px-6 sm:px-10 py-3 sm:py-4 bg-[#00E5FF] text-[#050505] font-bold text-xs sm:text-sm hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: 'Orbitron, sans-serif' }}
                    >
                      Zapytaj o usługę
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call‑to‑Action */}
      <section className="relative py-16 sm:py-24 lg:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <h2
              className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-8 leading-tight"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Gotowy na <span className="text-[#00E5FF]">Współpracę?</span>
            </h2>
            <p
              className="text-sm sm:text-lg lg:text-xl text-[#C0C7CF] mb-8 sm:mb-12 max-w-2xl mx-auto px-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Skontaktuj się z nami i omów szczegóły swojego projektu
            </p>
            <a
              href="/kontakt"
              className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-[#00E5FF] text-[#050505] font-bold text-sm sm:text-lg hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              Rozpocznij projekt
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="relative py-16 sm:py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <div className="bg-[#111111] border border-[#00E5FF]/20 p-5 sm:p-6 lg:p-8 hover:border-[#00E5FF] transition-all duration-300">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00E5FF]/10 border border-[#00E5FF] mb-4 sm:mb-6">
                <i className="ri-phone-line text-2xl sm:text-3xl text-[#00E5FF]"></i>
              </div>
              <h3
                className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 leading-tight"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Telefon
              </h3>
              <p
                className="text-[#C0C7CF] text-xs sm:text-sm mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <a href="tel:+48600978050" className="text-[#00E5FF] hover:underline cursor-pointer">+48 600 978 050</a><br />
                <a href="tel:+48790788033" className="text-[#00E5FF] hover:underline cursor-pointer">+48 790 788 033</a><br />
                <a href="tel:+48790333565" className="text-[#00E5FF] hover:underline cursor-pointer">+48 790 333 565</a>
              </p>
            </div>

            <div className="bg-[#111111] border border-[#00E5FF]/20 p-5 sm:p-6 lg:p-8 hover:border-[#00E5FF] transition-all duration-300">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00E5FF]/10 border border-[#00E5FF] mb-4 sm:mb-6">
                <i className="ri-mail-line text-2xl sm:text-3xl text-[#00E5FF]"></i>
              </div>
              <h3
                className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 leading-tight"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Email
              </h3>
              <p
                className="text-[#C0C7CF] text-xs sm:text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <a href="mailto:jgladysz@eureka.sanok.pl" className="text-[#00E5FF] hover:underline cursor-pointer">jgladysz@eureka.sanok.pl</a><br />
                <a href="mailto:kkomarski@eureka.sanok.pl" className="text-[#00E5FF] hover:underline cursor-pointer">kkomarski@eureka.sanok.pl</a>
              </p>
            </div>

            <div className="bg-[#111111] border border-[#00E5FF]/20 p-5 sm:p-6 lg:p-8 hover:border-[#00E5FF] transition-all duration-300 sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00E5FF]/10 border border-[#00E5FF] mb-4 sm:mb-6">
                <i className="ri-map-pin-line text-2xl sm:text-3xl text-[#00E5FF]"></i>
              </div>
              <h3
                className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 leading-tight"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Lokalizacja
              </h3>
              <p
                className="text-[#C0C7CF] text-xs sm:text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Zabłotce 146<br />
                38-500 Sanok
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
